import React, { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gql } from '@apollo/client';
import List from '#/common/components/List';
import type { Manufacturer } from '#/graphql/types';
import { getManufacturerList } from '#/graphql/queries';
import columns from '../ManufacturerDataGrid/columns';
import { Filter } from '#/common/components/DataGrid';
import useToast from '#/common/hooks/useToast';
import useManufacturerDelete from '../../hooks/useDeleteManufacturerMutation';
import { List as DxList } from 'devextreme-react/list';
import { inUseStatuses } from '../../utils/constants';

const getManufacturerListQuery = gql(getManufacturerList);

export interface ManufacturerListProps {
  permanentFilters?: Filter[];
}

const ManufacturerList: React.FC<ManufacturerListProps> = (props) => {
  const { permanentFilters } = props;

  const { confirm, alert } = useToast();
  const { deleteManufacturer } = useManufacturerDelete();
  const list = useRef<DxList | null>(null);

  const deleteItem = useCallback(
    async (items?: Manufacturer[], clearSelectedItems?: () => void) => {
      const filteredItemsForDelete = items?.filter(
        (manufacturer) =>
          manufacturer.inUseStatus === inUseStatuses.newlyCreated.key &&
          !(manufacturer.thirdPartyId && manufacturer.thirdPartySource),
      );

      if (!items?.length || !filteredItemsForDelete?.length) {
        alert(
          'warning',
          'No deletable manufacturers found. Only "Newly Created" ones can be deleted.',
        );
        return undefined;
      }

      if (filteredItemsForDelete.length < items.length) {
        const sure = await confirm(
          'error',
          `Some selected manufacturers are not in 'Newly Created' status and can not be deleted.`,
        );
        if (!sure) {
          return;
        }
      } else {
        const sure = await confirm('error', `Are you sure you want to delete these manufacturers?`);
        if (!sure) {
          return;
        }
      }

      await deleteManufacturer(items.map((item) => item.id).join(','));
      list.current && list.current.instance.reload();
      clearSelectedItems && clearSelectedItems();
    },
    [deleteManufacturer],
  );

  const navigate = useNavigate();

  const openManufacturer = useCallback(
    (manufacturer: Manufacturer) => {
      navigate(`/assets/manufacturers/${manufacturer.id}`);
    },
    [navigate],
  );

  return (
    <List<Manufacturer>
      setRef={(ref) => (list.current = ref)}
      titleKey="name"
      onClick={openManufacturer}
      permanentFilters={permanentFilters}
      getItemsQuery={getManufacturerListQuery}
      subtitleKey="description"
      bodyFields={[
        { 
          key: 'contactPerson', 
          label: 'Contact Person',
          labelKey: 'contact-person' 
        },
        { 
          key: 'contactEmail', 
          label: 'Contact Email',
          labelKey: 'contact-email' 
        },
      ]}
      columnList={columns}
      isActionDeleteOnly
      floatingButtonOnClick={deleteItem}
    />
  );
};

export default React.memo(ManufacturerList);
